import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Clock,
  CheckCircle2,
  Play,
  User,
  RefreshCw,
  ListChecks,
  MoreHorizontal,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import { yardApi, YardPickList } from '@/api/services/yard.api';
import {
  PageHeader,
  MetricCard,
  StatusBadge,
  DataTable,
  TableFilters,
  KPIGrid,
} from '@/components/common';
import type { Column } from '@/components/common';
import { motion } from 'framer-motion';
import { stagger, fadeUp } from '@/lib/animations';

export default function YardPicking() {
  const navigate = useNavigate();
  const [pickLists, setPickLists] = useState<YardPickList[]>([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    pending: 0,
    inProgress: 0,
    completedToday: 0,
    totalTasks: 0,
  });

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');

  const fetchPickLists = useCallback(async () => {
    try {
      setLoading(true);
      const params: Record<string, string> = {};
      if (statusFilter) params.status = statusFilter;

      const response = await yardApi.picking.getAll(params);
      setPickLists(Array.isArray(response) ? response : response?.data || []);
    } catch (error) {
      setPickLists([]);
      console.error('Error fetching pick lists:', error);
      toast.error('Failed to load pick lists');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);
  const fetchStats = useCallback(async () => {
    try {
      const response = await yardApi.picking.getStats();
      const defaults = { pending: 0, inProgress: 0, completedToday: 0, totalTasks: 0 };
      const statsData = typeof response === 'object' && response !== null ? response : {};
      setStats({ ...defaults, ...statsData });
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, []);

  useEffect(() => {
    fetchPickLists();
    fetchStats();
  }, [fetchPickLists, fetchStats]);

  const handleStartPickList = async (pickList: YardPickList) => {
    try {
      await yardApi.picking.start(pickList.id);
      toast.success('Picking started');
      fetchPickLists();
      fetchStats();
    } catch (error) {
      console.error('Error starting pick list:', error);
      toast.error('Failed to start picking');
    }
  };

  const handleCompletePickList = async (pickList: YardPickList) => {
    try {
      await yardApi.picking.complete(pickList.id);
      toast.success('Pick list completed');
      fetchPickLists();
      fetchStats();
    } catch (error) {
      console.error('Error completing pick list:', error);
      toast.error('Failed to complete pick list');
    }
  };

  const calculateProgress = (pickList: YardPickList) => {
    if (!pickList.lines || pickList.lines.length === 0) return 0;
    const completed = pickList.lines.filter((line) => line.status === 'COMPLETED').length;
    return Math.round((completed / pickList.lines.length) * 100);
  };

  const filteredPickLists = pickLists.filter((pickList) => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const matchesNumber = pickList.pickListNumber?.toLowerCase().includes(query);
      const matchesSource = pickList.sourceType?.toLowerCase().includes(query);
      if (!matchesNumber && !matchesSource) return false;
    }
    return true;
  });

  const pickListColumns: Column<YardPickList>[] = [
    {
      key: 'pickListNumber',
      header: 'Pick List #',
      sortable: true,
      linkTo: (pickList) => `/yard/picking/${pickList.id}`,
    },
    {
      key: 'sourceType',
      header: 'Source',
      render: (pickList) => (
        <div>
          <Badge variant="outline">{pickList.sourceType}</Badge>
          {pickList.sourceId && (
            <p className="text-xs text-muted-foreground mt-1">{pickList.sourceId.slice(0, 8)}...</p>
          )}
        </div>
      ),
    },
    {
      key: 'tasks',
      header: 'Tasks',
      render: (pickList) => <span>{pickList.lines?.length || 0} tasks</span>,
    },
    {
      key: 'progress',
      header: 'Progress',
      render: (pickList) => {
        const progress = calculateProgress(pickList);
        return (
          <div className="flex items-center gap-2 min-w-[100px]">
            <Progress value={progress} className="h-2" />
            <span className="text-sm text-muted-foreground">{progress}%</span>
          </div>
        );
      },
    },
    {
      key: 'priority',
      header: 'Priority',
      sortable: true,
      render: (pickList) => (
        <Badge
          variant={
            pickList.priority <= 3
              ? 'destructive'
              : pickList.priority <= 6
                ? 'outline'
                : 'secondary'
          }
        >
          {pickList.priority <= 3 ? 'High' : pickList.priority <= 6 ? 'Medium' : 'Low'}
        </Badge>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      render: (pickList) => <StatusBadge status={pickList.status} />,
    },
    {
      key: 'assignedTo',
      header: 'Assigned To',
      render: (pickList) =>
        pickList.assignedTo ? (
          <div className="flex items-center gap-2">
            <User className="h-4 w-4 text-muted-foreground" />
            <span>{pickList.assignedTo.fullName}</span>
          </div>
        ) : (
          <span className="text-muted-foreground">Unassigned</span>
        ),
    },
    {
      key: 'actions',
      header: '',
      className: 'text-right',
      render: (pickList) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" onClick={(e) => e.stopPropagation()}>
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {pickList.status === 'PENDING' && (
              <DropdownMenuItem onClick={() => handleStartPickList(pickList)}>
                <Play className="h-4 w-4 mr-2" />
                Start Picking
              </DropdownMenuItem>
            )}
            {pickList.status === 'IN_PROGRESS' && (
              <DropdownMenuItem onClick={() => handleCompletePickList(pickList)}>
                <CheckCircle2 className="h-4 w-4 mr-2" />
                Complete Pick List
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  return (
    <motion.main className="space-y-5" variants={stagger} initial="hidden" animate="show">
      <motion.div variants={fadeUp}>
        <PageHeader
          title="Picking"
          description="Pick lists issued against orders and kits, with progress and the picker assigned."
          actions={
            <Button
              variant="outline"
              onClick={() => {
                fetchPickLists();
                fetchStats();
              }}
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          }
        />
      </motion.div>

      {/* KPI Cards */}
      <motion.div variants={fadeUp}>
        <KPIGrid columns={4}>
          <MetricCard
            label="Pending Pick Lists"
            value={stats.pending}
            icon={<Clock className="h-5 w-5" />}
            sentiment="warning"
          />
          <MetricCard
            label="In Progress"
            value={stats.inProgress}
            icon={<Play className="h-5 w-5" />}
            sentiment="info"
          />
          <MetricCard
            label="Completed Today"
            value={stats.completedToday}
            icon={<CheckCircle2 className="h-5 w-5" />}
            sentiment="success"
          />
          <MetricCard
            label="Total Tasks"
            value={stats.totalTasks}
            icon={<ListChecks className="h-5 w-5" />}
            sentiment="neutral"
          />
        </KPIGrid>
      </motion.div>

      {/* Filters */}
      <motion.div variants={fadeUp}>
        {/* Pick Lists Table */}
        <DataTable<YardPickList>
          toolbar={
            <TableFilters
              search={searchQuery}
              onSearchChange={setSearchQuery}
              searchPlaceholder="Search by pick list number or source..."
              filters={[
                {
                  id: 'status',
                  label: 'Status',
                  value: statusFilter,
                  options: [
                    { value: 'PENDING', label: 'Pending' },
                    { value: 'IN_PROGRESS', label: 'In Progress' },
                    { value: 'COMPLETED', label: 'Completed' },
                    { value: 'PARTIALLY_COMPLETED', label: 'Partial' },
                  ],
                  onChange: setStatusFilter,
                  placeholder: 'Status',
                },
              ]}
            />
          }
          onRowClick={(pickList) => navigate(`/yard/picking/${pickList.id}`)}
          pagination
          data={filteredPickLists}
          columns={pickListColumns}
          loading={loading}
          emptyTitle="No pick lists found"
          emptyDescription="No pick lists match your current filters."
        />
      </motion.div>
    </motion.main>
  );
}
